import React, { useCallback } from 'react';

import Menu from 'components/base/Menu/Menu';
import { ReactComponent as CaretLeft } from 'icons/icon-caret-left.svg';
import { ReactComponent as Chevron } from 'icons/icon-chevron.svg';
import CustomerIcon from '../../../base/CustomerIcon/CustomerIcon';
import { DSPCustomers } from '../shared/customerProptypes';
import { IToggleOption } from 'components/data-containers/PositionsTable/TableControls/DSPToggle';

import pluralize from 'helpers/pluralize';

import {
  LayoutWrapper,
  ButtonContent,
  SideButton,
  ListItemContent,
} from './DSPDropdown.style';

const customerValues = ['all', ...DSPCustomers];

interface IDSPDropdownProps {
  selectedCustomer: IToggleOption;
  options: IToggleOption[];
  setSelectedCustomer: (customer: IToggleOption) => void;
  isIconDisplayed: boolean;
  disabled?: boolean;
  type?: string;
}

const DSPDropdown: React.FC<IDSPDropdownProps> = (props) => {
  const {
    selectedCustomer,
    options,
    setSelectedCustomer,
    disabled,
    isIconDisplayed,
    type = 'chart',
  } = props;

  /**
   * Finds the previous or next customer index or returns `-1`.
   *
   * @param {Number} operant this number is applied to the current index
   *                 to get the next index.
   */
  const getNewIndex = useCallback(
    (operant) => {
      const isSelectedCustomer = (customer: any): any =>
        customer.value === selectedCustomer.value;

      const currentIndex = options.findIndex(isSelectedCustomer);
      const index = currentIndex + operant;

      if (index < 0) return -1;
      if (index > options.length - 1) return -1;

      return index;
    },
    [options, selectedCustomer.value]
  );

  const nextIndex = getNewIndex(1);
  const previousIndex = getNewIndex(-1);

  return (
    <LayoutWrapper>
      <SideButton
        disabled={previousIndex < 0 || disabled}
        onClick={(): any =>
          previousIndex >= 0 && setSelectedCustomer(options[previousIndex])
        }
      >
        <span>
          <CaretLeft />
        </span>
      </SideButton>

      <Menu
        items={options}
        selectedItem={selectedCustomer}
        onChange={(option: any): any => setSelectedCustomer(option)}
        disabled={disabled || options.length <= 1}
        isIconDisplayed={isIconDisplayed}
        Item={MenuItem}
        Button={MenuButton}
        type={type}
      />

      <SideButton
        flipIcon
        disabled={nextIndex < 0 || disabled}
        onClick={(): any =>
          nextIndex >= 0 && setSelectedCustomer(options[nextIndex])
        }
      >
        <span>
          <CaretLeft />
        </span>
      </SideButton>
    </LayoutWrapper>
  );
};

interface IMenuButtonProps {
  selectedItem: any;
  isOpen: boolean;
  isIconDisplayed: boolean;
  type?: string;
}

const MenuButton: React.FC<IMenuButtonProps> = (props) => {
  const { selectedItem, isOpen, isIconDisplayed, type } = props;
  const withIcon = customerValues.includes(selectedItem.value);
  const categoryType = type === 'chart' ? type : selectedItem?.value;
  const property = type === 'chart' ? 'positionsCount' : 'count';

  return (
    <ButtonContent isOpen={isOpen}>
      {isIconDisplayed && withIcon && (
        <CustomerIcon aria-hidden customer={selectedItem.value} />
      )}
      <div>
        <span className="value">{selectedItem.label}</span>
        <span className="amount">
          {selectedItem[property]}{' '}
          {pluralize(categoryType, selectedItem[property])}
        </span>
      </div>
      <Chevron className="chevron" aria-hidden />
    </ButtonContent>
  );
};

interface IMenuItemProps {
  item: any;
  isIconDisplayed: boolean;
  type?: string;
}

const MenuItem: React.FC<IMenuItemProps> = (props) => {
  const { item, isIconDisplayed, type } = props;
  const withIcon = customerValues.includes(item.value);
  const entityType = type === 'chart' ? type : item?.value;
  const property = type === 'chart' ? 'positionsCount' : 'count';

  return (
    <ListItemContent>
      {isIconDisplayed && withIcon && (
        <CustomerIcon aria-hidden customer={item.value} />
      )}
      <div>
        <span className="value">{item.label}</span>
        <span className="amount">
          {item[property]} {pluralize(entityType, item[property])}
        </span>
      </div>
    </ListItemContent>
  );
};

export default DSPDropdown;
